import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Lead } from '@/modules/radar/types';
import { Client } from '@/modules/clients/types';
import { mockLeads, generateMassiveLeads, generateTestLeads, dataStore } from '@/data';
import { calculateLeadsPositions } from '@/utils/mockData';
import { addQualificationData } from '@/utils/generateQualificationData';
import { useTesting } from './TestingContext';

/**
 * @deprecated DataContext is deprecated. Use LeadsContext and ClientsContext instead.
 *
 * Migration guide:
 * - For leads data: use `useLeadsContext()` from './LeadsContext'
 * - For clients data: use `useClientsContext()` from './ClientsContext'
 *
 * This context is kept temporarily for backward compatibility and will be removed in future versions.
 */
interface DataContextType {
  // Leads
  leads: Lead[];
  setLeads: React.Dispatch<React.SetStateAction<Lead[]>>;
  leadCount: number;
  updateLead: (updatedLead: Lead) => void;

  // Clients
  clients: Client[];
  setClients: React.Dispatch<React.SetStateAction<Client[]>>;
  clientCount: number;
  updateClient: (updatedClient: Client) => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children }: { children: ReactNode }) {
  const { config: testingConfig, getTotalLeads } = useTesting();

  // Show deprecation warning on mount
  useEffect(() => {
    if (import.meta.env.DEV) {
    }
  }, []);

  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadCount, setLeadCount] = useState(6);

  const [clients, setClients] = useState<Client[]>([]);
  const [clientCount, setClientCount] = useState(15);

  // Initialize data on mount
  useEffect(() => {
    const initializeData = () => {
      // Generate leads based on testing config
      const totalLeads = getTotalLeads();
      let generatedLeads: Lead[] = [];

      const dataSize = testingConfig.dataSize || 'mock'; // Default to 'mock' if not set

      if (dataSize === 'mock') {
        generatedLeads = mockLeads;
      } else if (dataSize === 'small') {
        generatedLeads = generateTestLeads(100);
      } else if (dataSize === 'medium') {
        generatedLeads = generateMassiveLeads(500);
      } else if (dataSize === 'large') {
        generatedLeads = generateMassiveLeads(1000);
      } else if (dataSize === 'xl') {
        generatedLeads = generateMassiveLeads(5000);
      } else if (dataSize === 'custom') {
        generatedLeads = generateMassiveLeads(totalLeads);
      } else {
        // Fallback: use mock leads if dataSize is undefined or unknown
        generatedLeads = mockLeads;
      }

      // Add qualification data if enabled
      if (testingConfig.useQualificationData) {
        generatedLeads = addQualificationData(generatedLeads);
      }

      // Calculate radar positions
      const leadsWithPositions = calculateLeadsPositions(generatedLeads);

      // Update state
      setLeads(leadsWithPositions);
      setLeadCount(leadsWithPositions.length);

      // Load clients
      const loadedClients = dataStore.getAllClients();
      setClients(loadedClients);
      setClientCount(loadedClients.length);
    };

    initializeData();
  }, [testingConfig, getTotalLeads]);

  const updateLead = (updatedLead: Lead) => {
    setLeads(prevLeads =>
      prevLeads.map(lead => (lead.id === updatedLead.id ? updatedLead : lead))
    );
  };

  const updateClient = (updatedClient: Client) => {
    setClients(prevClients =>
      prevClients.map(client => (client.id === updatedClient.id ? updatedClient : client))
    );
  };

  const value: DataContextType = {
    leads,
    setLeads,
    leadCount,
    updateLead,
    clients,
    setClients,
    clientCount,
    updateClient,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

/**
 * @deprecated Use useLeadsContext() or useClientsContext() instead.
 *
 * This hook is deprecated and will be removed in future versions.
 * Use specialized contexts for better performance and maintainability:
 * - For leads: import { useLeadsContext } from './LeadsContext'
 * - For clients: import { useClientsContext } from './ClientsContext'
 */
export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }

  // Log deprecation warning in development
  if (import.meta.env.DEV) {
  }

  return context;
}
